import React, { useEffect, useRef, useState } from 'react';
import { View, Text, ScrollView, TouchableOpacity, ActivityIndicator } from 'react-native-web';
import { toast } from 'react-hot-toast';
import { cloneDeep } from 'lodash';
import { useRoute } from '@react-navigation/native';
import * as Animatable from 'react-native-animatable';

// Custom Components
import { NavBar, EmptyTextSection, ReviewItem } from '../../components';

// APIs
import { getTruckReviews } from '../../modules';

// Configs
import GLOBAL from '../../configs/config_global';

// Navigation
import * as RootNavigation from '../../navigation/RootNavigation';
import routeKeys from '../../navigation/RouteKeys';
import LoadingView from '../../components/component_loading_view';

export function Reviews() {
    const { onReplyRead, onReviewUpdated } = useRoute()?.params || {};

    // region Refs
    const delayValue = useRef(150);
    // endregion

    // region Local State
    const [isLoading, setLoading] = useState(true);
    const [loadingError, setLoadingError] = useState(null);
    const [reviewData, setReviewData] = useState([]);
    const [currentPage, setCurrentPage] = useState(0);
    const [lastPage, setLastPage] = useState(0);
    // endregion

    // region Component Mounting
    useEffect(() => {
        goFetchReviews();
    }, []);
    // endregion

    // region Function - Fetch Review Data
    const goFetchReviews = () => {
        setLoading(true);
        getTruckReviews(currentPage + 1, 10)
            .then((resultObj) => {
                setLoadingError(null);
                setCurrentPage(Number(resultObj.currentPage));
                setLastPage(Number(resultObj.lastPage));
                setReviewData(reviewData.concat(resultObj.data));
            })
            .catch((error) => {
                setLoadingError(error);
                (currentPage > 0) && toast('Unable to fetch more data from server.<br/><br/>Error message: ' + error);
            })
            .finally(() => setLoading(false));
    }
    // endregion

    // region Function - On Review Updated
    const _onReviewUpdated = (newReviewItem) => {
        const tempArr = cloneDeep(reviewData);
        const reviewIndex = reviewData.findIndex((item) => item.id === newReviewItem.id);

        if (reviewIndex > -1) {
            tempArr.splice(reviewIndex, 1, newReviewItem);
            setReviewData(tempArr);
        }
    }
    // endregion

    // region Function - On Review Pressed
    const _onReviewPressed = (item) => {
        RootNavigation.navigate(routeKeys.REVIEW_DETAILS, {
            reviewData: item,
            onReplyRead,
            onReviewUpdated: (newReviewItem) => _onReviewUpdated(newReviewItem)
        });
    }
    // endregion

    // region Render Review Item
    const renderReviewItem = (item, index) => {
        const currentDelay = delayValue.current * (index - (9 * (currentPage - 1)));
        return(
            <Animatable.View
                key={`reviewItem_${item.id}`}
                animation={'fadeInRight'} delay={currentDelay} duration={600}
                style={{ alignSelf: 'stretch', marginHorizontal: 24, marginTop: 24, backgroundColor: 'white', borderRadius: 6, borderColor: GLOBAL.COLOR.DIVIDER }}>
                <TouchableOpacity
                    onPress={() => _onReviewPressed(item)}
                    style={{ alignSelf: 'stretch' }}>
                    <ReviewItem
                        reviewData={item}
                        onReplyToVendorPressed={null}
                        showTotalResponses={true}
                        maxLines={10}
                    />
                </TouchableOpacity>
            </Animatable.View>
        );
    }
    // endregion

    // region MAIN RENDER
    return(
        <View style={{ width: '100%', flex: 1 }}>
            {/* Nav Bar */}
            <NavBar
                leftButtonOnPress={() => RootNavigation.goBack()}
                title={'Reviews'}
                leftButtonIconName={'arrow-back'}
            />

            {/* Main Content */}
            <View style={{ flex: 1, width: '100%' }}>
                {
                    (isLoading && (currentPage === 0)) ?
                        <LoadingView isLoading={true} />
                        :
                        (
                            (currentPage === 0 && !!loadingError?.trim()) ?
                                <View style={{ flex: 1, width: '100%', backgroundColor: GLOBAL.COLOR.BACKGROUND_GREY, paddingHorizontal: 24 }}>
                                    <Text style={{ fontSize: 18, textAlign: 'center', color: GLOBAL.COLOR.TEXT_GRAY }}>
                                        Failed to load reviews, please try again later.
                                        {'\n\n'}
                                        Error message:
                                        {'\n'}
                                        {loadingError}
                                    </Text>
                                </View>
                                :
                                (
                                    reviewData.length > 0 ?
                                        <ScrollView
                                            style={{ flex: 1, width: '100%', backgroundColor: GLOBAL.COLOR.BACKGROUND_GREY }}
                                            contentContainerStyle={{ paddingBottom: (currentPage === lastPage) ? 24 : 0 }}>
                                            {/* Review List */}
                                            {reviewData.map((item, index) => renderReviewItem(item, index))}

                                            {/* Load More Button */}
                                            {
                                                (currentPage < lastPage) &&
                                                <TouchableOpacity
                                                    disabled={isLoading}
                                                    onPress={() => goFetchReviews()}
                                                    style={{ width: '100%', height: 64, marginTop: 24, backgroundColor: GLOBAL.COLOR.MAIN, alignItems: 'center', justifyContent: 'center', flexDirection: 'row' }}>
                                                    {isLoading && <ActivityIndicator color={'white'} size={24} />}

                                                    {/* Text */}
                                                    <Text style={{ color: 'white', fontSize: 16, fontWeight: 'bold', marginLeft: isLoading ? 16 : 0 }}>
                                                        {isLoading ? 'Fetching more reviews...' : `Load more reviews (${(lastPage - currentPage) * 10} remaining)`}
                                                    </Text>
                                                </TouchableOpacity>
                                            }
                                        </ScrollView>
                                        :
                                        <EmptyTextSection
                                            title={'No reviews yet'}
                                            subTitle={'Ask your loyal fans to review you on Truckster!'}
                                        />
                                )
                        )
                }
            </View>
        </View>
    );
    // endregion
}
